import { formatNumber } from "@/v2-common/utils/utils.formatting"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Badge from "@/v2-backoffice-client/components/badge"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import UsageTrendBadge from "@/v2-backoffice-client/usage/UsageTrendBadge"
import {
  formatAppCode,
  formatTrendDetail,
  getAppCodeColor,
} from "@/v2-backoffice-client/usage/usage.constants"
import UsageReportCard from "@/v2-backoffice-client/usage/report/UsageReportCard"
import UsageReportKpi from "@/v2-backoffice-client/usage/report/UsageReportKpi"
import UsageReportSection from "@/v2-backoffice-client/usage/report/UsageReportSection"
import { useUsageLicenseDeniedCustomerQuery } from "@/v2-backoffice-client/usage/denied/usage.deniedCustomer.query"
import { useUsageLicenseDeniedUserQuery } from "@/v2-backoffice-client/usage/denied/usage.deniedUser.query"
import {
  LICENSE_DENIED_FEATURE_LIST_LIMIT,
  LICENSE_DENIED_SECTION_TOOLTIP,
} from "@/v2-backoffice-client/usage/denied/usage.denied.constants"
import {
  deriveLicenseDeniedWindow,
  hasLicenseDenied,
  type UsageLicenseDeniedFeatureRow,
} from "@/v2-backoffice-client/usage/denied/usage.denied.utils"
import UsageLicenseDeniedFeatureList from "@/v2-backoffice-client/usage/denied/UsageLicenseDeniedFeatureList"
import UsageLicenseDeniedMembersTable, {
  type UsageLicenseDeniedMember,
} from "@/v2-backoffice-client/usage/denied/UsageLicenseDeniedMembersTable"
import { usageKpiGridClass } from "@/v2-backoffice-client/usage/report/usage.report.utils"

export type UsageLicenseDeniedSummaryProps = {
  /** Scope to a customer's active users. Exactly one scope prop is set. */
  customerId?: number
  /** Scope to one user. */
  userId?: number
  /** The customer's or person's name, for the title. */
  subject: string
}

const EMPTY_FEATURES: UsageLicenseDeniedFeatureRow[] = []
const EMPTY_MEMBERS: UsageLicenseDeniedMember[] = []

// What the customer's (or the person's) people tried to use and the license
// did not carry — a click on a locked console feature, an MCP sign-in
// without a seat — any product, per feature. Usage says what they do with
// what they have; this says what they want and do not have, which is the
// upsell conversation. On the Overview; nothing renders for a scope where
// nobody has met a lock, which is most scopes.
function UsageLicenseDeniedSummary(props: UsageLicenseDeniedSummaryProps) {
  const { customerId, userId, subject } = props

  const customerQuery = useUsageLicenseDeniedCustomerQuery({
    skip: customerId == null,
    variables: { customerId: customerId ?? 0 },
  })
  const userQuery = useUsageLicenseDeniedUserQuery({
    skip: userId == null,
    variables: { userId: userId ?? 0 },
  })

  const scope =
    customerQuery.data?.getUsageLicenseDeniedByCustomer ??
    userQuery.data?.getUsageLicenseDeniedByUser
  const loading = customerQuery.loading || userQuery.loading
  const error = customerQuery.error ?? userQuery.error

  const features = scope?.Features ?? EMPTY_FEATURES
  const members =
    customerQuery.data?.getUsageLicenseDeniedByCustomer.Members ?? EMPTY_MEMBERS
  const summary = deriveLicenseDeniedWindow(features)
  const people28d = members.filter((member) => member.Attempts28d > 0).length

  if (loading && !scope) return null
  if (!hasLicenseDenied(features)) {
    return error ? <ErrorMessage error={error} /> : null
  }

  return (
    <UsageReportSection
      eyebrow="License"
      title={`What ${subject} asked for and does not have`}
      info={LICENSE_DENIED_SECTION_TOOLTIP}
      lead="Features people here tried to use that their license does not include — a locked button in Fusion, an MCP sign-in without a seat. Each one is something they wanted; together they are the upsell conversation."
      aside={summary.apps.map((app) => (
        <Badge key={app} variant="outline" size="xs">
          <span
            className="mr-1 inline-block size-2 rounded-full"
            style={{ backgroundColor: getAppCodeColor(app) }}
          />
          {formatAppCode(app)}
        </Badge>
      ))}
    >
      <ErrorMessage error={error} />

      <div className={usageKpiGridClass(3)}>
        <UsageReportKpi
          label="Refused attempts"
          tone={summary.attempts28d > 0 ? "warning" : "default"}
          value={
            <>
              {summary.attempts28d > 0 ? (
                formatNumber(summary.attempts28d)
              ) : (
                <span className="text-muted-foreground">0</span>
              )}
              <UsageTrendBadge
                trendPct={summary.trendPct}
                detail={formatTrendDetail(
                  summary.attempts28d,
                  summary.attemptsPrev28d,
                )}
              />
            </>
          }
          sub={[
            "in the last 28 days, against the four weeks before",
            summary.lastAt ? (
              <>
                last <RelativeDate date={summary.lastAt} />
              </>
            ) : null,
          ]}
        />
        {customerId != null && (
          <UsageReportKpi
            label="People asking"
            value={people28d}
            sub={[
              "in the last 28 days",
              `${members.length} in the last 8 weeks`,
            ]}
          />
        )}
        <UsageReportKpi
          label="Features asked for"
          value={summary.features28d}
          sub={[
            "in the last 28 days",
            `${features.length} in the last 8 weeks`,
          ]}
        />
      </div>

      <div className="usage-report-grid usage-report-grid--two">
        <UsageReportCard
          title="Locked features they tried"
          caption="Refused attempts in the last 28 days per feature, the people behind each and when it last happened."
          tone="warning"
        >
          <UsageLicenseDeniedFeatureList
            features={features}
            limit={LICENSE_DENIED_FEATURE_LIST_LIMIT}
            showPeople={customerId != null}
            emptyText="No refused attempt in the last 28 days."
          />
        </UsageReportCard>
        {customerId != null && members.length > 0 && (
          <UsageReportCard
            title="Who is asking"
            caption="Most attempts first — the person to name in the conversation sits on top. A name opens their panel."
          >
            <UsageLicenseDeniedMembersTable members={members} />
          </UsageReportCard>
        )}
      </div>
    </UsageReportSection>
  )
}

export default UsageLicenseDeniedSummary
